import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AuthProfile } from './models/auth.model';

@Component({
  selector: 'app-organization-card',
  imports: [CommonModule, RouterModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      [id]="'org-card-' + account().uid"
      class="group relative overflow-hidden flex items-center gap-4 p-4 rounded-[24px] cursor-pointer border border-white/40 dark:border-white/10 bg-white/40 dark:bg-black/40 backdrop-blur-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.04)] dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.3)] hover:shadow-[0_12px_40px_0_rgba(0,0,0,0.08)] dark:hover:shadow-[0_12px_40px_0_rgba(0,0,0,0.5)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
      [routerLink]="['/profile', account().uid]"
      (click)="cardClick.emit(account())"
      tabindex="0"
    >
      <!-- Premium background liquid glass gradient overlay -->
      <div
        class="absolute inset-0 bg-gradient-to-tr from-ios-teal/5 via-transparent to-ios-purple/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
      ></div>

      <!-- Avatar/Logo Container -->
      <div
        [id]="'org-logo-container-' + account().uid"
        class="w-12 h-12 rounded-[16px] overflow-hidden bg-gradient-to-br from-ios-teal/10 to-ios-blue/10 dark:from-ios-teal/20 dark:to-ios-blue/20 shrink-0 border border-white/20 dark:border-white/10 relative z-10 shadow-sm flex items-center justify-center"
      >
        @if (account().photoURL) {
          <img
            [src]="account().photoURL"
            [alt]="account().displayName"
            class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            referrerpolicy="no-referrer"
          />
        } @else {
          <mat-icon
            [id]="'org-fallback-icon-' + account().uid"
            class="text-ios-teal dark:text-ios-teal/80 text-2xl !w-6 !h-6 flex items-center justify-center font-bold"
            >corporate_fare</mat-icon
          >
        }
        <!-- Online/Active indicator -->
        <div
          class="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white dark:border-zinc-900 z-20"
          [class.bg-emerald-500]="account().isOnline"
          [class.bg-gray-400]="!account().isOnline"
        ></div>
      </div>

      <!-- Organization Info Block -->
      <div class="flex-1 min-w-0 relative z-10">
        <div class="flex items-center gap-1.5 min-w-0">
          <h4
            [id]="'org-name-' + account().uid"
            class="text-base font-bold text-gray-900 dark:text-white group-hover:text-ios-blue transition-colors duration-300 leading-tight"
          >
            {{ (account().displayName || "").charAt(0).toUpperCase() + (account().displayName || "").substring(1).toLowerCase() }}
          </h4>
          <mat-icon
            [id]="'org-verified-icon-' + account().uid"
            class="text-ios-blue !text-[16px] !w-4 !h-4 flex items-center justify-center shrink-0"
            title="Verified Organization"
            >verified</mat-icon
          >
        </div>
        <div class="flex items-center gap-2 mt-0.5">
          <p
            [id]="'org-username-' + account().uid"
            class="text-xs font-medium text-gray-500 dark:text-zinc-400"
          >
            {{ account().username ? "@" + account().username?.toLowerCase() : "@organization" }}
          </p>
        </div>
      </div>

      <!-- Arrow Action Indicator -->
      <div class="relative z-10 shrink-0 flex items-center justify-center">
        <mat-icon
          [id]="'org-arrow-' + account().uid"
          class="text-gray-400 dark:text-zinc-500 group-hover:text-ios-teal opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0 duration-300 !text-[20px] !w-[20px] !h-[20px]"
          >chevron_right</mat-icon
        >
      </div>
    </div>
  `
})
export class OrganizationCard {
  account = input.required<AuthProfile>();
  cardClick = output<AuthProfile>();
}
